import lodash from 'lodash'
import { getEnvParameter } from '@/api/settings/request'
import * as tenanciesApi from '@/api/tenancies/request'
import { getSameBuildingUsers, getUsersByPermsAndBuildings } from '@/api/users/request'
import store from '@/store'
import { PermTypeMapKeys } from '@/utils/aclHelper'
import { ccdRouteNames, EmailTypes, tenancyViewTabs } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import notification from '@/utils/notification'
import userHelper from '@/utils/userHelper'
import { getPrimaryEmail } from '../users/util'
import { SEND_EMAIL, SEND_TO_ENGINEER } from './consts'
import { buildEmail, getLink, getLinkToTab, getPublicLink, getTenantDashboardLink } from './util'

function getTenancyInfo () {
  const tenancyInfo = { manager: {} }
  const currentTenancy = store.getters['ccd/currentTenancy']
  tenancyInfo.id = currentTenancy.id
  tenancyInfo.companyName = lodash.get(currentTenancy, 'company.name')
  tenancyInfo.building = currentTenancy.building
  tenancyInfo.buildingFullName = currentTenancy.building.fullName
  tenancyInfo.buildingId = deep(currentTenancy, 'building.id')
  tenancyInfo.type = currentTenancy.type
  tenancyInfo.tradeName = deep(currentTenancy, 'tradeName')
  tenancyInfo.manager.name = userHelper.getUserName(deep(currentTenancy, 'manager.username').split('@')[0], deep(currentTenancy, 'manager.displayName'))

  return tenancyInfo
}

async function getSameBuildingEmails (buildingId, tenancyType, PermTypeKey) {
  const sameBuildingUsers = await getSameBuildingUsers(buildingId, tenancyType, false, PermTypeKey)
  let emails = []
  sameBuildingUsers.forEach(item => {
    if (item.email) {
      emails.push(item.email)
    } else {
      emails.push(item.username)
    }
  })
  return emails
}
async function inviteTenancyInfo (ccUserPerm, isInviteContractor) {
  const metaData = {}
  const tenancy = store.getters['ccd/currentTenancy']
  try {
    if (ccUserPerm) {
      const users = await getSameBuildingUsers(tenancy.building.id, tenancy.type, false, ccUserPerm)
      metaData.cc = lodash.uniq(users.map(user => user.email || user.username))
    }
  } catch (err) {
    notification.handleResponseError(err, 'sendmail', i18n.t('error.sendmail.default'))
    throw err
  }
  metaData.tenancyId = tenancy.id
  metaData.linkToAccess = isInviteContractor
    ? getTenantDashboardLink()
    : getPublicLink(ccdRouteNames.ccdTenancyTenantView, tenancy.id)
  metaData.tenantCompanyName = lodash.get(tenancy, 'company.name')
  metaData.tradeName = lodash.get(tenancy, 'tradeName')
  metaData.subsidiary = lodash.get(tenancy, 'physicalSite.subsidiary')
  metaData.fullBuildingName = tenancy.building.fullName
  metaData.premises = tenancy.premisesDetailList.map(premises => premises.detail).join(',')
  return metaData
}

const tenancyNotification = {
  writeCreateEmail: async (extMetaData) => {
    let emails = []
    const tenancyInfo = getTenancyInfo()
    const toEmails = await getSameBuildingEmails(tenancyInfo.buildingId, tenancyInfo.type, PermTypeMapKeys.HOTB_APPROVE)
    let sendTo = ''
    toEmails.forEach(email => {
      sendTo += email + ';'
    })
    tenancyInfo.type = tenancyInfo.type === 'Office' ? 'O' : 'R'
    const tenancyLink = getLink('ccdTenancyView', tenancyInfo.id, process.env.ENGINEER_HOST_NAME)
    const metaData = {
      tenancyInfo,
      tenancyLink,
    }
    const template = EmailTypes.tenancies.create
    buildEmail(emails, sendTo, template, metaData, SEND_TO_ENGINEER)
    return emails
  },
  requestForInvitation: async (extMetaData) => {
    const emails = []
    const tenancy = store.getters['ccd/currentTenancy']
    const tenancyTabKeys = Object.values(tenancyViewTabs)
      .filter((value) => {
        if (value !== tenancyViewTabs.sustainabilityInitiatives) return true
        return store.getters['regions/enableGreenOperationFeature']
      })
      .filter((value) => {
        if (value !== tenancyViewTabs.allProjects) return true
        const bProjectEnabled = store.getters['regions/enableTenantProjectsFeature']
        return bProjectEnabled
      })
      .filter((value) => {
        if (value !== tenancyViewTabs.standaloneWorkPermits) return true
        const bWorkPermitsEnabled = store.getters['regions/enableWorkPermitsFeature']
        return bWorkPermitsEnabled
      })
    const tabIndex = tenancyTabKeys.length - 1
    const engineerEntry = store.getters['settings/engineerEntry']
    const linkToAccess = getLinkToTab('ccdTenancyView', { id: tenancy.id }, { tabIndex }, engineerEntry)
    const metaData = {
      tenancyId: tenancy.id,
      tradeName: lodash.get(tenancy, 'tradeName'),
      companyName: lodash.get(tenancy, 'company.name'),
      buildingFullName: lodash.get(tenancy, 'building.fullName'),
      linkToAccess,
    }
    const users = await getUsersByPermsAndBuildings(PermTypeMapKeys.INVITE_TENANCY_MASTER, [tenancy.building.code]) || []
    const to = new Set()
    users.forEach(user => {
      const email = getPrimaryEmail(user)
      to.add(email)
    })
    buildEmail(emails, to, EmailTypes.tenancies.requestForInvitation, { ...extMetaData, ...metaData }, SEND_EMAIL)
    return emails
  },
  inviteTenantMasterUser: async (extMetaData) => {
    const emails = []
    const metaData = await inviteTenancyInfo()
    const passwordExpiresIn = await getEnvParameter('token.passwordExpiresIn')
    metaData.passwordExpiresIn = dateUtil.transTimeUnitByLang(passwordExpiresIn)
    buildEmail(emails, extMetaData.to, EmailTypes.tenancies.inviteTenantMasterUser, {...extMetaData, ...metaData}, SEND_EMAIL)
    return emails
  },
  inviteSubAccount: async (extMetaData) => {
    const emails = []
    const metaData = await inviteTenancyInfo()
    const passwordExpiresIn = await getEnvParameter('token.passwordExpiresIn')
    metaData.passwordExpiresIn = dateUtil.transTimeUnitByLang(passwordExpiresIn)
    buildEmail(emails, extMetaData.to, EmailTypes.tenancies.inviteSubAccount, {...extMetaData, ...metaData}, SEND_EMAIL)
    return emails
  },
  contactInfoConfirm: async (extMetaData) => {
    const emails = []
    let tenancy = store.getters['ccd/currentTenancy'] || {}
    const vendorEntry = store.getters['settings/vendorEntry']
    if (tenancy.id !== extMetaData.id) {
      tenancy = await tenanciesApi.getTenancy(extMetaData.id)
    }
    const recipients = tenancy.accounts.filter(account => {
      const item = account.TenancyAccount
      return !!lodash.get(item, 'invitedAt') && (lodash.get(item, 'isAdmin', false) ||
      !!lodash.get(item, 'perms.TenancyRecord'))
    })
    const to = recipients.map(recipient => recipient.username)
    const name = recipients.map(recipient => recipient.displayName).join(', ')
    let metaData = {}
    metaData.tradeName = tenancy.tradeName
    metaData.buildingFullName = tenancy.building.fullName
    metaData.name = name
    metaData.to
    metaData.link = getLinkToTab(ccdRouteNames.ccdTenancyTenantView, { id: extMetaData.id }, { tabIndex: tenancyViewTabs.allContacts }, vendorEntry)
    metaData.subsidiary = i18n.t('system.title')
    buildEmail(emails, to, EmailTypes.jobs.contactInfoConfirm, {...extMetaData, ...metaData}, SEND_EMAIL)
    return emails
  },
  inviteContractor: async (extMetaData) => {
    const emails = []
    const metaData = await inviteTenancyInfo(null, true)
    const passwordExpiresIn = await getEnvParameter('token.passwordExpiresIn')
    metaData.passwordExpiresIn = dateUtil.transTimeUnitByLang(passwordExpiresIn)
    buildEmail(emails, extMetaData.to, EmailTypes.tenancies.inviteContractor, {...extMetaData, ...metaData}, SEND_EMAIL)
    return emails
  },
}

export default tenancyNotification
